import React from 'react';
import { Box, Icon, Link, Popover } from '@amzn/awsui-components-core';
import { DateTime } from 'luxon';
import {
  ContentWorkflow,
  WorkflowInfo,
  WorkflowState,
  getSLADueDate,
  TechDomainDisplayNames,
} from '@amzn/awscat-pattern-library-service-model';
import kebabCase from 'kebab-case';
import {
  canTechWriterEdit,
  extractUserAlias,
  findPublicationWorkflow,
  hasPreviousAPGWorkflows,
  isBarRaiserEditable,
  isPartnerContributed,
  isPendingBarRaiserWorkflow,
  isPendingToDistributeWorkflow,
  isPendingToFinalizeWorkflow,
  isRefinalizeWorkflow,
} from '../../api/UtilsApi';
import ContentAudienceBadge from './ContentAudienceBadge';
import TechWriterContentViewerlink from './TechWriterContentViewerLink';
import FinalizingContentViewerLink from './FinalizingContentViewerLink';
import { getContentWorkflowTagNames } from '../utils/WorkflowTagUtils';
import ProgressDiagram from '../content/ProgressDiagram';
import { determineProgress } from '../../api/WorkflowProgressUtil';
import WorkflowStateBadge from './WorkflowStateBadge';
import ContentEngagementAdminLink from '../content-engagement/ContentEngagementAdminLink';
import WorkflowTagBadges from './WorkflowTagBadges';
import constants from '../../common/constants';
import {
  barRaiserStatusDisplayNames,
  contentAudienceBadgeDisplayNames,
  contentPriorityDisplayNames,
  techWriterStatusDisplayNames,
  workflowStateDisplayNames,
} from '../utils/DisplayNames';
import { ColumnTypes } from './ContentWorkflowTableFilters';
import LocalStorage from '../../common/LocalStorage';
import ContentInfoPopover from './ContentInfoPopover';
import ContentNotesPopover from './ContentNotesPopover';
import ReadOnlyContentViewerLink from './ReadOnlyContentViewerLink';
import ContentPrioritySelect from './ContentPrioritySelect';
import RouterPaths from '../../common/RouterPaths';
import BarRaiserContentViewerLink from './BarRaiserContentViewerLink';
import UserTitle from '../../model/UserTitle';
import StatusSelect from './StatusSelect';
import WorkflowSLADueDate from './WorkflowSLADueDate';
import TechWriterAssigneeSelect from './TechWriterAssigneeSelect';
import BarRaiserAssigneeSelect from './BarRaiserAssigneeSelect';
import { WorkflowTableIdentity } from '../../common/CookieRegistry';
import TableSettingOptions from '../../model/TableSettingOptions';

const autoPrioritizeFlagIconSvg = '/images/ic_flagged.svg';
const engagementIconSvg = '/images/ic_contact_group_active.svg';

export interface AdditionalContentData {
  noteStats?: Map<string, number>;
  contentCoauthorMap?: Map<string, string[]>;
}

export class ContentWorkflowTableItem {
  workflow: ContentWorkflow;
  private additionalContentData?: AdditionalContentData;
  get contentId() {
    return this.workflow.contentId;
  }
  get mainTechnology() {
    // Content without template metadata (hidden by the dashboard's default filters but surfaced when
    // they are cleared) has no draftTemplateMetadata, so guard it rather than throw into the error boundary.
    return TechDomainDisplayNames.get(this.workflow.draftTemplateMetadata?.mainTechDomain);
  }
  get intendedAudience() {
    return contentAudienceBadgeDisplayNames.get(this.workflow.contentAudience);
  }
  get author() {
    return extractAuthor(this.workflow);
  }
  get techWriter() {
    return extractTechWriter(this.workflow);
  }
  get barRaiser() {
    return extractBarRaiser(this.workflow);
  }
  get techWriterStatus() {
    return extractTechWriterStatus(this.workflow);
  }
  get barRaiserStatus() {
    return extractBarRaiserStatus(this.workflow);
  }
  get progress() {
    return determineProgress(this.workflow);
  }
  get techWriterQueue() {
    return extractTechWriterQueue(this.workflow);
  }
  get currentDraftState() {
    return workflowStateDisplayNames.get(this.workflow.draftState);
  }
  get currentPublicationState() {
    // @ts-expect-error TS2345 - strict migration
    return workflowStateDisplayNames.get(this.workflow.publicationState);
  }
  get publications() {
    return this.workflow.publicationWorkflows.map((publication) => publication.state);
  }
  get lastPublicationDate() {
    return this.workflow.publicationDate?.toISO() ?? '';
  }
  get lastApgPublicationDate() {
    return this.workflow.apgPublicationDate?.toISO() ?? '';
  }
  get lastApgPublicationVersion() {
    return this.workflow.lastApgPublicationId;
  }
  get engagements() {
    return this.workflow.engagementCount;
  }
  get likes() {
    return this.workflow.likesCount;
  }
  get rank() {
    return this.workflow.rank;
  }
  get approvalsCount() {
    return extractApprovals(this.workflow);
  }
  get disapprovalsCount() {
    return extractDisapprovals(this.workflow);
  }
  get isTestData() {
    return this.workflow.isTestData;
  }
  get isLocked() {
    return this.workflow.lock !== undefined;
  }
  get tags() {
    return getContentWorkflowTagNames(this.workflow);
  }
  get techWriterPool() {
    return isInTechWriterPool(this.workflow);
  }
  get refinalize() {
    return isInRefinalizePool(this.workflow);
  }
  get hasCode() {
    return hasCodeLinks(this.workflow);
  }
  get autoPrioritized() {
    return isAutoPrioritizedWorkflow(this.workflow);
  }
  get notesCount() {
    return this.additionalContentData?.noteStats?.get(this.workflow.contentId) ?? 0;
  }
  get contentName() {
    return this.workflow.draftContentMetadata.contentName;
  }
  get contentType() {
    return this.workflow.draftContentMetadata.contentType;
  }
  get contentPriority() {
    return contentPriorityDisplayNames.get(this.workflow.contentPriority);
  }
  get barRaiserEditable() {
    return isBarRaiserEditable(this.workflow);
  }
  get partnerContributed() {
    return isPartnerContributed(this.workflow, this.additionalContentData?.contentCoauthorMap);
  }
  get slaSubmitted() {
    return this.workflow.draftStateLastUpdated?.toISO() ?? '';
  }
  get slaDueDate() {
    return getSLADueDate(this.workflow);
  }
  get lastQualityScore() {
    return this.workflow.lastQualityScore;
  }
  get lastQualityScoreDate() {
    return this.workflow.lastQualityScoreDate;
  }
  get updateRequestsCount() {
    return this.workflow.updateRequestsCount;
  }
  constructor(wf: ContentWorkflow, additionalContentData?: AdditionalContentData) {
    this.workflow = wf;
    this.additionalContentData = additionalContentData;
  }
}

export enum WorkflowAttribute {
  TechWriterId = 'techWriterId',
  TechWriterStatus = 'techWriterStatus',
}

export interface TableUpdateProps {
  tableId: string;
}

export interface VisibleContentOption {
  id: string;
  label: string;
  editable?: boolean;
  visible?: boolean;
  hidePreference?: boolean;
}

export enum TablePreferenceCheckboxType {
  StickyHeader = 'StickyHeader',
  ClearFiltersOnRefresh = 'ClearFiltersOnRefresh',
}

export const ContentInfoCol = {
  cell: (i: ContentWorkflowTableItem) => <ContentInfoPopover workflow={i.workflow} />,
  header: (
    <p title="Content details" className="table-column-header">
      <Icon url="/images/ic_information.svg" size="normal" variant="normal" alt="Content details" />
    </p>
  ),
  filterLabel: 'Content details',
  id: 'contentInfo',
  width: 60,
  minWidth: 60,
};

export const ContentInfoSelOpt = {
  editable: false,
  id: 'contentInfo',
  label: 'Content details',
  visible: true,
  hidePreference: false,
};

export const ContentIdCol = {
  cell: (i: ContentWorkflowTableItem) => i.contentId,
  header: (
    <p className="table-column-header" title="Content ID">
      Content ID
    </p>
  ),
  filterLabel: 'Content ID',
  id: 'contentId',
  sortingField: 'contentId',
  width: 300,
  minWidth: 300,
};

export const ContentIdSelOpt = {
  editable: true,
  id: 'contentId',
  label: 'Content ID',
  visible: false,
};

export const NotesCol = {
  cell: (i: ContentWorkflowTableItem) => (
    <ContentNotesPopover workflow={i.workflow} notesCount={i.notesCount} />
  ),
  header: (
    <p title="Notes" className="table-column-header">
      <Icon name="contact" size="normal" variant="normal" />
    </p>
  ),
  filterLabel: 'Notes',
  id: 'notes',
  width: 80,
  minWidth: 80,
};

export const NotesSelOpt = {
  editable: true,
  id: 'notes',
  label: 'Notes',
  visible: true,
};

const extractContentNameLink = (i: ContentWorkflowTableItem) => {
  const contentNameLink =
    canTechWriterEdit(i.workflow) &&
    (isPendingToFinalizeWorkflow(i.workflow) || isRefinalizeWorkflow(i.workflow)) ? (
      <FinalizingContentViewerLink
        contentId={i.contentId}
        workflowId={i.workflow.publicationWorkflowId}
        metadata={i.workflow.draftContentMetadata}
      />
    ) : (
      <TechWriterContentViewerlink
        contentId={i.contentId}
        metadata={i.workflow.draftContentMetadata}
      />
    );
  const autoPrioritizeIcon = isAutoPrioritizedWorkflow(i.workflow) && (
    <Icon url={autoPrioritizeFlagIconSvg} size="normal" variant="normal" alt="Auto prioritized" />
  );
  return (
    <div className="hide-overflow-text">
      {autoPrioritizeIcon}
      <span className="padding-left-5">{contentNameLink}</span>
    </div>
  );
};

export const ContentNameCol = {
  cell: (i: ContentWorkflowTableItem) => extractContentNameLink(i),
  header: (
    <p className="table-column-header" title="Content title">
      Title
    </p>
  ),
  filterLabel: 'Title',
  id: 'contentName',
  minWidth: 300,
  sortingField: 'contentName',
};

export const BarRaiserContentNameCol = {
  cell: (i: ContentWorkflowTableItem) => {
    const mainTechDomain = i.workflow.draftTemplateMetadata.mainTechDomain;
    const techDomainUrl = mainTechDomain ? kebabCase(mainTechDomain) : '';
    const publishedNeedReview =
      i.workflow.draftState === WorkflowState.UNMODIFIED && !!i.workflow.publicationDate;
    if (i.workflow.draftState === WorkflowState.REVIEWED || publishedNeedReview) {
      return (
        <BarRaiserContentViewerLink
          contentId={i.contentId}
          metadata={i.workflow.draftContentMetadata}
          navigateBackTo={RouterPaths.BAR_RAISER_DASHBOARD_PREFIX + techDomainUrl}
          useLegacyBarRaiserViewer={publishedNeedReview}
        />
      );
    }
    return (
      <ReadOnlyContentViewerLink
        contentId={i.contentId}
        metadata={i.workflow.draftContentMetadata}
        navigateBackTo={RouterPaths.BAR_RAISER_DASHBOARD}
      />
    );
  },
  header: (
    <p className="table-column-header" title="Content title">
      Title
    </p>
  ),
  filterLabel: 'Title',
  id: 'contentName',
  minWidth: 300,
  sortingField: 'contentName',
};

export const ContentNameSelOpt = {
  editable: true,
  id: 'contentName',
  label: 'Title',
  visible: true,
};

export const ContentTypeCol = {
  cell: (i: ContentWorkflowTableItem) => i.contentType,
  header: (
    <p className="table-column-header" title="Content type">
      Type
    </p>
  ),
  filterLabel: 'Type',
  id: 'contentType',
  sortingField: 'contentType',
  minWidth: 100,
  width: 100,
};

export const ContentTypeSelOpt = {
  editable: true,
  id: 'contentType',
  label: 'Type',
  visible: true,
};

export const MainTechnologyCol = {
  cell: (i: ContentWorkflowTableItem) => i.mainTechnology,
  header: (
    <p className="table-column-header" title="Main technology">
      Technology
    </p>
  ),
  filterLabel: 'Technology',
  id: 'mainTechnology',
  sortingField: 'mainTechnology',
  width: 180,
  minWidth: 180,
};

export const MainTechnologySelOpt = {
  editable: true,
  id: 'mainTechnology',
  label: 'Technology',
  visible: true,
};

export const ContentAudienceCol = {
  cell: (i: ContentWorkflowTableItem) => (
    <ContentAudienceBadge contentAudience={i.workflow.contentAudience} />
  ),
  header: (
    <p className="table-column-header" title="Intended audience">
      Audience
    </p>
  ),
  filterLabel: 'Audience',
  id: 'intendedAudience',
  sortingField: 'intendedAudience',
  width: 150,
  minWidth: 150,
};

export const ContentAudienceSelOpt = {
  editable: true,
  id: 'intendedAudience',
  label: 'Audience',
  visible: true,
};

const extractAuthor = (i: ContentWorkflow) => {
  return extractUserAlias(i.ownerId);
};

export const AuthorCol = {
  allowLineWrap: true,
  cell: (i: ContentWorkflowTableItem) => i.author,
  header: (
    <p className="table-column-header" title="Content author">
      Author
    </p>
  ),
  filterLabel: 'Author',
  id: 'author',
  sortingField: 'author',
  width: 150,
  minWidth: 150,
};

export const AuthorSelOpt = {
  editable: true,
  id: 'author',
  label: 'Author',
  visible: true,
};

export const extractTechWriter = (i: ContentWorkflow) => {
  const { techWriterId } = i;
  return techWriterId ? extractUserAlias(techWriterId) : '';
};

export const TechWriterCol = () => {
  return {
    cell: (i: ContentWorkflowTableItem) =>
      isPendingToFinalize(i.workflow) ? (
        <TechWriterAssigneeSelect workflow={i.workflow} />
      ) : (
        i.techWriter
      ),
    header: (
      <p className="table-column-header" title="Tech writer">
        Tech writer
      </p>
    ),
    filterLabel: 'Tech writer',
    id: 'techWriter',
    sortingField: 'techWriter',
    minWidth: 250,
    width: 250,
  };
};

export const TechWriterSelOpt = {
  editable: true,
  id: 'techWriter',
  label: 'Tech writer',
  visible: true,
};

export const extractBarRaiser = (i: ContentWorkflow) => {
  const { barRaiserId } = i;
  return barRaiserId ? extractUserAlias(barRaiserId) : '';
};

export const BarRaiserCol = (forPublishedContents?: boolean) => {
  return {
    cell: (i: ContentWorkflowTableItem) =>
      isPendingBarRaiserWorkflow(i.workflow) ? (
        <BarRaiserAssigneeSelect
          workflow={i.workflow}
          forPublishedContents={forPublishedContents}
        />
      ) : (
        i.barRaiser
      ),
    header: (
      <p className="table-column-header" title="Bar raiser">
        Bar raiser
      </p>
    ),
    filterLabel: 'Bar raiser',
    id: 'barRaiser',
    sortingField: 'barRaiser',
    minWidth: 250,
    width: 250,
  };
};

export const BarRaiserSelOpt = {
  editable: true,
  id: 'barRaiser',
  label: 'Bar raiser',
  visible: true,
};

const extractTechWriterQueue = (i: ContentWorkflow, convertToString?: boolean) => {
  return isInTechWriterPool(i) && i.publicationDate
    ? convertToString
      ? i.publicationDate.toLocaleString(DateTime.DATE_FULL)
      : i.publicationDate.toISO()
    : '';
};

export const TechWriterQueueCol = {
  cell: (i: ContentWorkflowTableItem) => extractTechWriterQueue(i.workflow, true),
  header: (
    <p className="table-column-header" title="In TW queue since">
      In TW queue
    </p>
  ),
  filterLabel: 'In TW queue',
  id: 'techWriterQueue',
  sortingField: 'techWriterQueue',
  minWidth: 180,
  width: 180,
};

export const TechWriterQueueSelOpt = {
  editable: true,
  id: 'techWriterQueue',
  label: 'In TW queue',
  visible: true,
};

export const ProgressCol = {
  cell: (i: ContentWorkflowTableItem) => <ProgressDiagram workflow={i.workflow} />,
  header: (
    <p className="table-column-header" title="Progress">
      Progress
    </p>
  ),
  filterLabel: 'Progress',
  id: 'progress',
  minWidth: 200,
  width: 200,
  sortingField: 'progress',
};

export const ProgressColSelOpt = {
  editable: false,
  id: 'progress',
  label: 'Progress',
  visible: true,
};

export const SLASubmittedCol = {
  cell: (i: ContentWorkflowTableItem) =>
    isPendingBarRaiserWorkflow(i.workflow) ? i.slaSubmitted.substring(0, 10) : null,
  header: (
    <p className="table-column-header" title="SLA submitted">
      SLA submitted (UTC)
    </p>
  ),
  filterLabel: 'SLA submitted',
  id: 'slaSubmitted',
  minWidth: 160,
  width: 160,
  sortingField: 'slaSubmitted',
};

export const SLASubmittedSelOpt = {
  editable: true,
  id: 'slaSubmitted',
  label: 'SLA submitted',
  visible: true,
};

export const SLADueDateCol = {
  cell: (i: ContentWorkflowTableItem) =>
    isPendingBarRaiserWorkflow(i.workflow) ? <WorkflowSLADueDate workflow={i.workflow} /> : null,
  header: (
    <p className="table-column-header" title="SLA due date">
      SLA due date (UTC)
    </p>
  ),
  filterLabel: 'SLA due date',
  id: 'slaDueDate',
  minWidth: 150,
  width: 150,
  sortingField: 'slaDueDate',
};

export const SLADueDateSelOpt = {
  editable: true,
  id: 'slaDueDate',
  label: 'SLA due date',
  visible: true,
};

export const LastQualityScoreCol = {
  cell: (i: ContentWorkflowTableItem) => (i.lastQualityScore === -1 ? 'N/A' : i.lastQualityScore),
  header: (
    <p className="table-column-header" title="Last Quality Score">
      Last Quality Score
    </p>
  ),
  filterLabel: 'Last Quality Score',
  id: 'lastQualityScore',
  minWidth: 180,
  width: 180,
  sortingField: 'lastQualityScore',
};

export const LastQualityScoreSelOpt = {
  editable: false,
  id: 'lastQualityScore',
  label: 'Last Quality Score',
  visible: true,
};

export const LastQualityScoreDateCol = {
  cell: (i: ContentWorkflowTableItem) => (
    <div className="hide-overflow-text">
      {i.workflow.lastQualityScoreDate?.toString().substring(0, 10) ?? ''}
    </div>
  ),
  header: (
    <p className="table-column-header" title="Last Quality Score Date">
      Last Quality Score Date
    </p>
  ),
  filterLabel: 'Last Quality Score Date',
  id: 'lastQualityScoreDate',
  minWidth: 200,
  width: 200,
  sortingField: 'lastQualityScoreDate',
};

export const LastQualityScoreDateSelOpt = {
  editable: false,
  id: 'lastQualityScoreDate',
  label: 'Last Quality Score Date',
  visible: true,
};

const extractBarRaiserStatus = (i: ContentWorkflow) =>
  isPendingBarRaiserWorkflow(i)
    ? barRaiserStatusDisplayNames.get(
        // @ts-expect-error TS2345 - strict migration
        i.publicationWorkflows?.[0]?.barRaiserStatus ?? i.barRaiserStatus,
      )
    : '';

export const BarRaiserStatusCol = (forPublishedContents: boolean) => {
  return {
    cell: (i: ContentWorkflowTableItem) =>
      isPendingBarRaiserWorkflow(i.workflow) ? (
        <StatusSelect
          workflow={i.workflow}
          userTitle={UserTitle.BarRaiser}
          forPublishedContents={forPublishedContents}
        />
      ) : (
        ''
      ),
    header: (
      <p className="table-column-header" title="Bar raiser status">
        Bar raiser status
      </p>
    ),
    filterLabel: 'Bar raiser status',
    id: 'barRaiserStatus',
    minWidth: 275,
    width: 275,
    sortingField: 'barRaiserStatus',
  };
};

export const BarRaiserStatusSelOpt = {
  editable: true,
  id: 'barRaiserStatus',
  label: 'Bar raiser status',
  visible: true,
};

const extractTechWriterStatus = (i: ContentWorkflow) => {
  // @ts-expect-error TS2345 - strict migration
  return isPendingToFinalize(i) ? techWriterStatusDisplayNames.get(i.techWriterStatus) : '';
};

export const TechWriterStatusCol = () => {
  return {
    cell: (i: ContentWorkflowTableItem) =>
      isPendingToFinalize(i.workflow) ? (
        <StatusSelect workflow={i.workflow} userTitle={UserTitle.Techwriter} />
      ) : (
        ''
      ),
    header: (
      <p className="table-column-header" title="Tech writer status">
        Tech writer status
      </p>
    ),
    filterLabel: 'Tech writer status',
    id: 'techWriterStatus',
    minWidth: 275,
    width: 275,
    sortingField: 'techWriterStatus',
  };
};

export const TechWriterStatusSelOpt = {
  editable: true,
  id: 'techWriterStatus',
  label: 'Tech writer status',
  visible: true,
};

export const DraftStateCol = {
  cell: (i: ContentWorkflowTableItem) => <WorkflowStateBadge state={i.workflow.draftState} />,
  header: (
    <p className="table-column-header" title="Draft state">
      Draft state
    </p>
  ),
  filterLabel: 'Draft state',
  id: 'currentDraftState',
  sortingField: 'currentDraftState',
};

export const DraftStateSelOpt = {
  editable: true,
  id: 'currentDraftState',
  label: 'Draft state',
  visible: false,
};

export const PublicationStateCol = {
  // @ts-expect-error TS2769 - strict migration
  cell: (i: ContentWorkflowTableItem) => <WorkflowStateBadge state={i.workflow.publicationState} />,
  header: (
    <p className="table-column-header" title="Publication state">
      Publication state
    </p>
  ),
  filterLabel: 'Publication state',
  id: 'currentPublicationState',
  minWidth: 135,
  width: 135,
  sortingField: 'currentPublicationState',
};

export const PublicationStateSelOpt = {
  editable: true,
  id: 'currentPublicationState',
  label: 'Publication state',
  visible: false,
};

const renderWorkflowInfoLink = (wfInfo: WorkflowInfo) => {
  const name = `Version ${wfInfo.workflowId}:`;
  const href = `/content-history-viewer/${wfInfo.contentId}/${wfInfo.workflowId}`;
  if (wfInfo.state === WorkflowState.UNPUBLISHED) {
    return name;
  }
  return (
    <Link variant="secondary" href={href}>
      {name}
    </Link>
  );
};

export const PublicationsCol = {
  cell: (i: ContentWorkflowTableItem) =>
    i.workflow.publicationWorkflows.map((wf) => {
      return (
        <div key={wf.state} className="publication-info">
          <Box margin={{ bottom: 'xs' }}>
            <Box display="inline" margin={{ right: 'xs' }}>
              {renderWorkflowInfoLink(wf)}
            </Box>
            <Popover
              size="small"
              position="top"
              triggerType="custom"
              dismissButton={false}
              content={
                <>
                  <div>
                    {wf.approvals} {constants.APPROVALS}
                  </div>
                  <div>
                    {wf.disapprovals} {constants.DISAPPROVALS}
                  </div>
                </>
              }
            >
              <WorkflowStateBadge state={wf.state} />
            </Popover>
          </Box>
        </div>
      );
    }),
  header: (
    <p className="table-column-header" title="Current publications">
      Publications
    </p>
  ),
  filterLabel: 'Publications',
  id: 'publications',
  minWidth: 140,
  sortingField: 'publications',
};

export const PublicationsSelOpt = {
  editable: true,
  id: 'publications',
  label: 'Publications',
  visible: false,
};

export const LastPublicationDateCol = {
  cell: (i: ContentWorkflowTableItem) => (
    <div className="hide-overflow-text">
      {i.workflow.publicationDate?.toLocaleString(DateTime.DATETIME_FULL) ?? ''}
    </div>
  ),
  header: (
    <p className="table-column-header" title="Last publication">
      Last publication
    </p>
  ),
  filterLabel: 'Last publication',
  id: 'lastPublicationDate',
  sortingField: 'lastPublicationDate',
  minWidth: 270,
  width: 270,
};

export const LastPublicationDateSelOpt = (visibility: boolean = false) => {
  return {
    editable: true,
    id: 'lastPublicationDate',
    label: 'Last publication',
    visible: visibility,
  };
};

export const LastAPGPublicationDateCol = {
  cell: (i: ContentWorkflowTableItem) => (
    <div className="hide-overflow-text">
      {i.workflow.apgPublicationDate?.toLocaleString(DateTime.DATETIME_FULL) ?? ''}
    </div>
  ),
  header: (
    <p className="table-column-header" title="Last APG publication date">
      Last APG publication
    </p>
  ),
  filterLabel: 'Last APG publication',
  id: 'lastApgPublicationDate',
  sortingField: 'lastApgPublicationDate',
  minWidth: 180,
  width: 180,
};

export const LastAPGPublicationDateSelOpt = {
  editable: true,
  id: 'lastApgPublicationDate',
  label: 'Last APG publication',
  visible: true,
};

export const LastAPGPublicationVersionCol = {
  cell: (i: ContentWorkflowTableItem) => i.lastApgPublicationVersion,
  header: (
    <p className="table-column-header" title="Last APG publication version">
      Version
    </p>
  ),
  filterLabel: 'Version',
  id: 'lastApgPublicationVersion',
  sortingField: 'lastApgPublicationVersion',
};

export const LastAPGPublicationVersionSelOpt = {
  editable: true,
  id: 'lastApgPublicationVersion',
  label: 'Version',
  visible: false,
};

export const EngagementsCol = {
  cell: (i: ContentWorkflowTableItem) => (
    <div className="position-absolute">
      {' '}
      <ContentEngagementAdminLink workflow={i.workflow} />
    </div>
  ),
  header: (
    <p title="Engagements" className="table-column-header">
      <Icon url={engagementIconSvg} variant="normal" alt="Engagements" />
    </p>
  ),
  filterLabel: 'Engagements',
  id: 'engagements',
  sortingField: 'engagements',
  type: ColumnTypes.NUMBER,
  width: 110,
  minWidth: 110,
};

export const EngagementsSelOpt = {
  editable: true,
  id: 'engagements',
  label: 'Engagements',
  visible: true,
};

export const LikesCol = {
  cell: (i: ContentWorkflowTableItem) => i.workflow.likesCount,
  header: (
    <p title="Likes" className="table-column-header">
      <Icon url="/images/thumb-up-voted.svg" variant="normal" alt="Likes" />
    </p>
  ),
  filterLabel: 'Likes',
  id: 'likes',
  sortingField: 'likes',
  type: ColumnTypes.NUMBER,
  width: 80,
  minWidth: 80,
};

export const LikesSelOpt = {
  editable: true,
  id: 'likes',
  label: 'Likes',
  visible: true,
};

export const RankCol = {
  cell: (i: ContentWorkflowTableItem) => i.rank,
  header: (
    <p className="table-column-header" title="Rank">
      Rank
    </p>
  ),
  filterLabel: 'Rank',
  id: 'rank',
  sortingField: 'rank',
  type: ColumnTypes.NUMBER,
};

export const RankSelOpt = {
  editable: true,
  id: 'rank',
  label: 'Rank',
  visible: false,
};

const extractApprovals = (i: ContentWorkflow) => {
  return findPublicationWorkflow(i)?.approvals ?? i.approvals;
};

export const ApprovalsCol = {
  cell: (i: ContentWorkflowTableItem) => i.approvalsCount,
  header: (
    <p className="table-column-header" title="Peer reviewer approvals">
      PR approvals
    </p>
  ),
  filterLabel: 'PR approvals',
  id: 'approvalsCount',
  sortingField: 'approvalsCount',
  type: ColumnTypes.NUMBER,
  minWidth: 150,
};

export const ApprovalsSelOpt = {
  editable: true,
  id: 'approvalsCount',
  label: 'PR approvals',
  visible: false,
};

const extractDisapprovals = (i: ContentWorkflow) => {
  return findPublicationWorkflow(i)?.disapprovals ?? i.disapprovals;
};

export const DisapprovalsCol = {
  cell: (i: ContentWorkflowTableItem) => i.disapprovalsCount,
  header: (
    <p className="table-column-header" title="Peer reviewer disapprovals">
      PR disapprovals
    </p>
  ),
  filterLabel: 'PR disapprovals',
  id: 'disapprovalsCount',
  sortingField: 'disapprovalsCount',
  type: ColumnTypes.NUMBER,
  minWidth: 150,
};

export const DisapprovalsSelOpt = {
  editable: true,
  id: 'disapprovalsCount',
  label: 'PR disapprovals',
  visible: false,
};

export const IsTestDataCol = {
  cell: (i: ContentWorkflowTableItem) => (i.isTestData ? <Icon name="status-positive" /> : null),
  header: (
    <p className="table-column-header" title="Test data">
      Test data
    </p>
  ),
  filterLabel: 'Test data',
  id: 'isTestData',
  sortingField: 'isTestData',
};

export const IsTestDataSelOpt = {
  editable: true,
  id: 'isTestData',
  label: 'Test data',
  visible: false,
};

export const IsLockedCol = {
  cell: (i: ContentWorkflowTableItem) =>
    i.workflow.lock ? (
      <Box>
        <Icon name="status-positive" /> (by {extractUserAlias(i.workflow.lock.acquiredBy)})
      </Box>
    ) : null,
  header: (
    <p className="table-column-header" title="Locked">
      Locked
    </p>
  ),
  filterLabel: 'Locked',
  id: 'isLocked',
  sortingField: 'isLocked',
};

export const IsLockedSelOpt = {
  editable: true,
  id: 'isLocked',
  label: 'Locked',
  visible: false,
};

export const WorkflowTagsCol = {
  cell: (i: ContentWorkflowTableItem) => <WorkflowTagBadges workflow={i.workflow} />,
  header: (
    <p className="table-column-header" title="Tags">
      Tags
    </p>
  ),
  filterLabel: 'Tags',
  id: 'tags',
  sortingField: 'tags',
};

export const WorfklowTagsSelOpt = {
  editable: true,
  id: 'tags',
  label: 'Tags',
  visible: true,
};

export const BarRaiserContentPriorityCol = () => {
  return {
    cell: (i: ContentWorkflowTableItem) =>
      i.barRaiserEditable ? <ContentPrioritySelect workflow={i.workflow} /> : i.contentPriority,
    header: (
      <p className="table-column-header" title={constants.PUBLIC_RELEASE_PRIORITY_TOOLTIP}>
        Public release priority
      </p>
    ),
    filterLabel: 'Public release priority',
    id: 'contentPriority',
    minWidth: 250,
    width: 250,
    sortingField: 'contentPriority',
    type: ColumnTypes.NUMBER,
  };
};

export const ContentPriorityCol = {
  cell: (i: ContentWorkflowTableItem) => i.contentPriority,
  header: (
    <p className="table-column-header" title={constants.PUBLIC_RELEASE_PRIORITY_TOOLTIP}>
      Public release priority
    </p>
  ),
  filterLabel: 'Public release priority',
  id: 'contentPriority',
  minWidth: 150,
  width: 150,
  sortingField: 'contentPriority',
  type: ColumnTypes.NUMBER,
};

export const ContentPrioritySelOpt = {
  editable: true,
  id: 'contentPriority',
  label: 'Public release priority',
  visible: true,
};

export const PartnerContributedCol = {
  cell: (i: ContentWorkflowTableItem) =>
    i.partnerContributed ? <Icon name="status-positive" /> : null,
  header: (
    <p className="table-column-header" title="Partner contributed">
      Partner contributed
    </p>
  ),
  id: 'partnerContributed',
  sortingField: 'partnerContributed',
  filterLabel: 'Partner contributed',
  type: ColumnTypes.BOOLEAN,
};

export const PartnerContributedSelOpt = {
  editable: true,
  id: 'partnerContributed',
  label: 'Partner contributed',
  visible: false,
};

const isPendingToFinalize = (wf: ContentWorkflow) => {
  return isPendingToFinalizeWorkflow(wf) && canTechWriterEdit(wf);
};

const isInTechWriterPool = (wf: ContentWorkflow) => {
  return (
    (isPendingToFinalizeWorkflow(wf) || isPendingToDistributeWorkflow(wf)) && canTechWriterEdit(wf)
  );
};

const isInRefinalizePool = (wf: ContentWorkflow) => {
  return isRefinalizeWorkflow(wf) && canTechWriterEdit(wf);
};
const hasCodeLinks = (wf: ContentWorkflow) => {
  return !!(wf.draftContentTypeMetadata.codeLinks?.length > 0);
};

const isAutoPrioritizedWorkflow = (wf: ContentWorkflow) => {
  return isPendingToFinalize(wf) && hasPreviousAPGWorkflows(wf);
};

export const TechWriterPoolCol = {
  cell: (i: ContentWorkflowTableItem) => i.techWriterPool,
  header: (
    <p className="table-column-header" title="Content in tech writer Pool">
      In TW Pool
    </p>
  ),
  filterLabel: 'In TW Pool',
  id: 'techWriterPool',
  sortingField: 'techWriterPool',
  type: ColumnTypes.BOOLEAN,
  group: 'tags',
};

export const RefinalizeCol = {
  cell: (i: ContentWorkflowTableItem) => i.refinalize,
  header: (
    <p className="table-column-header" title="Content to refinalize">
      To refinalize
    </p>
  ),
  filterLabel: 'To refinalize',
  id: 'refinalize',
  sortingField: 'refinalize',
  type: ColumnTypes.BOOLEAN,
  group: 'tags',
};

export const HasCodeCol = {
  cell: (i: ContentWorkflowTableItem) => i.hasCode,
  header: (
    <p className="table-column-header" title="Has code">
      Has code
    </p>
  ),
  filterLabel: 'Has code',
  id: 'hasCode',
  sortingField: 'hasCode',
  type: ColumnTypes.BOOLEAN,
  group: 'tags',
};

export const IsAutoPrioritizedCol = {
  cell: (i: ContentWorkflowTableItem) => i.autoPrioritized,
  header: (
    <p className="table-column-header" title="Auto prioritized">
      Auto prioritized
    </p>
  ),
  filterLabel: 'Auto prioritized',
  id: 'autoPrioritized',
  sortingField: 'autoPrioritized',
  type: ColumnTypes.BOOLEAN,
  group: 'tags',
};

export const UpdateRequestsCountCol = {
  cell: (i: ContentWorkflowTableItem) => i.updateRequestsCount,
  header: (
    <p className="table-column-header" title="Num of update requests">
      Num of update requests
    </p>
  ),
  filterLabel: 'Num of update requests',
  id: 'updateRequestsCount',
  sortingField: 'updateRequestsCount',
  type: ColumnTypes.NUMBER,
  group: 'tags',
  minWidth: 230,
};

export const UpdateRequestsCountSelOpt = {
  editable: false,
  id: 'updateRequestsCount',
  label: 'Num of update requests',
  visible: true,
};

export const ContentWorkflowV3CsvFormat = (
  tableId?: string,
): Array<{ header: string; value: (i: ContentWorkflowTableItem) => any }> => {
  return [
    {
      header: 'Content ID',
      value: (i) => i.contentId,
    },
    {
      header: 'Title',
      value: (i) => i.contentName,
    },
    {
      header: 'Type',
      value: (i) => i.contentType,
    },
    {
      header: 'Technology',
      value: (i) => i.mainTechnology,
    },
    {
      header: 'Audience',
      value: (i) => i.intendedAudience,
    },
    {
      header: 'Author',
      value: (i) => i.author,
    },
    {
      header: 'Bar raiser',
      value: (i) =>
        tableId === WorkflowTableIdentity.PublishedBarRaiserDashboard
          ? // @ts-expect-error TS2532 - strict migration
            i.workflow.publicationWorkflows[0].barRaiserId
          : i.barRaiser,
    },
    {
      header: 'Bar raiser status',
      value: (i) =>
        tableId === WorkflowTableIdentity.PublishedBarRaiserDashboard
          ? // @ts-expect-error TS2532 - strict migration
            i.workflow.publicationWorkflows[0].barRaiserStatus
          : i.barRaiserStatus,
    },
    {
      header: 'Last quality score',
      value: (i) => i.lastQualityScore,
    },
    {
      header: 'Last quality score Date',
      value: (i) => i.lastQualityScoreDate,
    },
    {
      header: 'Num of update requests',
      value: (i) => i.updateRequestsCount,
    },
    {
      header: 'Tech writer',
      value: (i) => i.techWriter,
    },
    {
      header: 'Tech Writer Status',
      value: (i) => i.techWriterStatus,
    },
    {
      header: 'In TW queue',
      value: (i) => i.techWriterQueue,
    },
    {
      header: 'Draft state',
      value: (i) => i.currentDraftState,
    },
    {
      header: 'Publication state',
      value: (i) => i.currentPublicationState,
    },
    {
      header: 'Last publication',
      value: (i) => i.lastPublicationDate,
    },
    {
      header: 'Last APG publication',
      value: (i) => i.lastApgPublicationDate,
    },
    {
      header: 'SLA submitted',
      value: (i) => i.slaSubmitted,
    },
    {
      header: 'SLA due date',
      value: (i) => (i.slaDueDate > 0 ? new Date(i.slaDueDate) : ''),
    },
    {
      header: 'Version',
      value: (i) => i.lastApgPublicationVersion,
    },
    {
      header: 'Engagements',
      // @ts-expect-error TS18048 - strict migration
      value: (i) => (i.engagements > 0 ? i.engagements : ''),
    },
    {
      header: 'Rank',
      value: (i) => i.rank,
    },
    {
      header: 'PR approvals',
      value: (i) => i.approvalsCount,
    },
    {
      header: 'PR disapprovals',
      value: (i) => i.disapprovalsCount,
    },
    {
      header: 'Test data',
      value: (i) => (i.isTestData ? 'Yes' : ''),
    },
    {
      header: 'Locked',
      value: (i) => (i.workflow.lock ? extractUserAlias(i.workflow.lock.acquiredBy) : ''),
    },
    {
      header: 'Tags',
      value: (i) => i.tags,
    },
    {
      header: 'Public release priority',
      value: (i) => i.contentPriority,
    },
    {
      header: 'Partner contributed',
      value: (i) => (i.partnerContributed ? 'Yes' : ''),
    },
  ];
};

export const DefaultContentSelectorOptions: VisibleContentOption[] = [
  ContentInfoSelOpt,
  NotesSelOpt,
  ContentIdSelOpt,
  SLASubmittedSelOpt,
  SLADueDateSelOpt,
  ContentNameSelOpt,
  ContentTypeSelOpt,
  MainTechnologySelOpt,
  WorfklowTagsSelOpt,
  ContentAudienceSelOpt,
  AuthorSelOpt,
  TechWriterSelOpt,
  ProgressColSelOpt,
  LastQualityScoreSelOpt,
  LastQualityScoreDateSelOpt,
  BarRaiserStatusSelOpt,
  TechWriterStatusSelOpt,
  ContentPrioritySelOpt,
  EngagementsSelOpt,
  TechWriterQueueSelOpt,
  LastAPGPublicationDateSelOpt,
  DraftStateSelOpt,
  PublicationStateSelOpt,
  PublicationsSelOpt,
  LastPublicationDateSelOpt(),
  LastAPGPublicationVersionSelOpt,
  RankSelOpt,
  ApprovalsSelOpt,
  DisapprovalsSelOpt,
  IsTestDataSelOpt,
  IsLockedSelOpt,
  PartnerContributedSelOpt,
  BarRaiserSelOpt,
  UpdateRequestsCountSelOpt,
];

export const DefaultPublishedBarRaiserSelectorOptions = [
  ...DefaultContentSelectorOptions.filter((column) => column.id !== 'lastPublicationDate'),
  LastPublicationDateSelOpt(true),
];

export const getTechwriterTableColumns = (props: TableUpdateProps) => {
  return [
    ContentInfoCol,
    NotesCol,
    ContentIdCol,
    ContentNameCol,
    ContentTypeCol,
    MainTechnologyCol,
    WorkflowTagsCol,
    ContentAudienceCol,
    AuthorCol,
    TechWriterCol(),
    ProgressCol,
    TechWriterStatusCol(),
    ContentPriorityCol,
    EngagementsCol,
    TechWriterQueueCol,
    LastAPGPublicationDateCol,
    DraftStateCol,
    PublicationStateCol,
    PublicationsCol,
    LastPublicationDateCol,
    LastAPGPublicationVersionCol,
    RankCol,
    ApprovalsCol,
    DisapprovalsCol,
    IsTestDataCol,
    IsLockedCol,
    TechWriterPoolCol,
    RefinalizeCol,
    HasCodeCol,
    IsAutoPrioritizedCol,
    PartnerContributedCol,
  ];
};

export const getBarRaiserPublishedDashboardTableColumns = (props: TableUpdateProps) => {
  return [
    ContentInfoCol,
    ContentIdCol,
    BarRaiserContentNameCol,
    ContentTypeCol,
    MainTechnologyCol,
    ContentAudienceCol,
    BarRaiserCol(true),
    BarRaiserStatusCol(true),
    ProgressCol,
    AuthorCol,
    UpdateRequestsCountCol,
    EngagementsCol,
    LastPublicationDateCol,
  ];
};

export const getBarRaiserDashboardTableColumns = (props: TableUpdateProps) => {
  return [
    ContentInfoCol,
    ContentIdCol,
    BarRaiserContentNameCol,
    SLASubmittedCol,
    SLADueDateCol,
    ContentTypeCol,
    MainTechnologyCol,
    ContentAudienceCol,
    BarRaiserCol(),
    BarRaiserContentPriorityCol(),
    BarRaiserStatusCol(false),
    AuthorCol,
    ProgressCol,
    LastQualityScoreCol,
    LastQualityScoreDateCol,
    DraftStateCol,
    PublicationStateCol,
    PublicationsCol,
    LastPublicationDateCol,
    LastAPGPublicationDateCol,
    LastAPGPublicationVersionCol,
    EngagementsCol,
    RankCol,
    ApprovalsCol,
    DisapprovalsCol,
    IsTestDataCol,
    IsLockedCol,
    WorkflowTagsCol,
    HasCodeCol,
    PartnerContributedCol,
  ];
};

export const DefaultTablePageSizeOptions = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '25',
    value: 25,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
  {
    label: '200',
    value: 200,
  },
  {
    label: '500',
    value: 500,
  },
];

export const DefaultTablePreferences: Partial<TableSettingOptions> = {
  pageSize: 25,
  wrapLines: false,
  stickyHeader: false,
  clearFiltersOnRefresh: false,
};

export const buildTableItems = (
  workflows: ContentWorkflow[],
  additionalContentData?: AdditionalContentData,
): ContentWorkflowTableItem[] => {
  return workflows.map((wf) => new ContentWorkflowTableItem(wf, additionalContentData));
};

export const getDefaultTablePreferenceCache = (tableId: string, tableSelectorOptions: any[]) => {
  const existingTablePreferenceCache = LocalStorage.getTableSettingOptions(tableId);
  if (existingTablePreferenceCache?.visibleColumns?.size > 0) {
    return existingTablePreferenceCache;
  } else {
    return Object.assign(existingTablePreferenceCache, {
      ...DefaultTablePreferences,
      visibleColumns: new Set(tableSelectorOptions.filter((c) => c.visible).map((c) => c.id)),
    });
  }
};

export const publishedBarRaiserDashboardDefaultSort = {
  defaultState: {
    sortingColumn: {
      sortingComparator: (a: ContentWorkflowTableItem, b: ContentWorkflowTableItem) => {
        const statusOrder = {
          UnpublishingRecommended: 6,
          BarRaiserReviewing: 4,
          NeedsNewBarRaiser: 5,
          AwaitingAuthorFeedback: 2,
          AwaitingCodeRepoApproval: 3,
          Other: 1,
        };
        // @ts-expect-error TS2538 - strict migration
        const statusA = statusOrder[a?.workflow?.publicationWorkflows?.[0]?.barRaiserStatus] ?? -1;
        // @ts-expect-error TS2538 - strict migration
        const statusB = statusOrder[b?.workflow?.publicationWorkflows?.[0]?.barRaiserStatus] ?? -1;

        // if status are both unpublish recommended, break tie by audience
        const audienceOrder = {
          PUBLIC: 4,
          EXTERNAL: 3,
          INTERNAL: 2,
          PARTNER: 1,
        };
        // Break unpublish recommended & no bar raiser status ties by highest content audience
        if (statusA === statusB && (statusA === 6 || statusA === -1)) {
          const audienceA = audienceOrder[a?.workflow?.contentAudience] ?? 0;
          const audienceB = audienceOrder[b?.workflow?.contentAudience] ?? 0;
          return audienceB - audienceA;
        }
        return statusB - statusA;
      },
    },
  },
};
